import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

import { IpSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { AuthService } from "../../../services/authservice";
import { MessageService } from "../../../services/messageservice";
import { DialogSelfServiceComponent } from "../../dialogs/dialog-selfservice/dialog-selfservice";
import { ModifyIpButton } from "../tabbed-detail-container/components/modify-ip-button/modify-ip-button";
import { openSelfserviceDialog } from "../tabbed-detail-container/open-selfservice-dialog";
import { TabbedDetailBase } from "../tabbed-detail-container/tabbed-detail-base";
import { descriptionForEntity } from "../tabbed-detail-container/tabbed-detail-container.helpers";
import { SelfserviceState } from "../tabbed-detail-container/tabbed-detail-container.types";
import { IpSapPanel } from "./components/ip-sap-panel/ip-sap-panel";

@Component({
  selector: "ip-detail-container",
  templateUrl: "ip-detail-container.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IpSapPanel, ModifyIpButton, TabbedDetailBase, TranslatePipe],
})
export class IpDetailContainer {
  private translate = inject(TranslateService);
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);
  private msgsrv = inject(MessageService);

  subscription = input.required<IpSubscriptionDetails>();
  protected ipRoutingType = computed(() => this.subscription().ipRoutingType);

  protected activeTabIndex = model(0);
  protected displayLoadingBanner = computed(() => this.auth.hasPendingStrongAction("ip-prefixes"));
  protected firewallEnabled = computed(() => this.subscription().firewallEnabled ?? false);
  protected showModifyButton = computed(() => this.auth.isSelfServiceEnabled && !this.firewallEnabled());
  protected isInfrastructureDelegate = computed(
    () =>
      this.auth.isCurrentOrganisation(this.subscription().customerId) && this.auth.hasRole(["Infraverantwoordelijke"]),
  );

  protected navTabs = computed(() =>
    this.subscription().saps.map((sap) => {
      const title = descriptionForEntity(sap.port);
      const base = sap.port?.locationDescription ?? sap.port?.customer?.fullname ?? "-";
      const subtitle =
        sap.vlanrange?.length ?
          `${base}, ${this.translate.instant("ServiceElement.VLAN")} ${sap.vlanrange}`
        : `${base}, ${this.translate.instant("Subscription.UntaggedPort")}`;
      return { title, subtitle };
    }),
  );

  protected activeSap = computed(() => {
    const saps = this.subscription().saps;
    const idx = this.activeTabIndex();
    return idx < saps.length ? saps[idx] : null;
  });

  private selfserviceState = signal<SelfserviceState | null>(null);
  private dialogRef: MatDialogRef<DialogSelfServiceComponent<IpSubscriptionDetails>> | undefined;

  protected openSelfserviceDialog(type: string) {
    const activeSap = this.activeSap();
    if (!activeSap) return;

    const ref = openSelfserviceDialog(
      type,
      this.dialog,
      this.auth,
      this.subscription(),
      activeSap,
      this.activeTabIndex(),
      this.selfserviceState,
      this.dialogRef,
    );
    if (ref) {
      this.dialogRef = ref as MatDialogRef<DialogSelfServiceComponent<IpSubscriptionDetails>>;
      this.dialogRef.afterClosed().subscribe((event: string) => {
        this.msgsrv.setSelfServiceMessage(event);
      });
    }
  }
}
